import store from "../../store/configuration";
import Configurator from "./Configurator";

export default class Field {
    static PathSeparator = '.'
    /**
     * @type {FormModelStore}
     */
    formModelStore = null;

    /**
     * Constructs a new Field.
     *
     * @param {String} name
     * @param {StepModel} step
     * @param {Boolean} supportWarnings
     * @param {FormModelStore} formModelStore
     * @param {Boolean} required
     */
    constructor(name, step, supportWarnings = true, formModelStore = null, required = false) {
        if (!step) throw new Error(`No step defined for field '${name}'`)

        this.formModelStore = formModelStore;
        this.required = required;
        this.name = name
        this.step = step
        this.supportWarnings = supportWarnings
        this.ref = null

        // Temporary property to distinguish these fields from old-style ones.
        this.__temporary_dis = 1
    }

    errorState = false;

    validationErrorMessage = '';

    set value(newValue) {
        this.setValue(newValue)
    }

    setValue(newValue) {
        if (this.formModelStore !== null) {
            this.formModelStore.setFormModelValue(this.getModelPath(), newValue);
            return;
        }
        store.setFormModelValue(this.getModelPath(), newValue)
    }

    // Getters for most commonly used properties.
    get value() {
        return this.getProperty('value')
    }

    get unit() {
        return this.getProperty('unit')
    }

    get visible() {
        try {
            return this.getProperty('visible')
        } catch (e) {

            // In some cases the fields may want to render before receiving server response.
            // This can not be allowed as store has not been updated at this point and new fields may not
            // be available, yet.
            return false
        }
    }

    get items() {
        return this.getProperty('items')
    }

    get label() {
        return this.getProperty('label')
    }

    get description() {
        return this.getProperty('description')
    }

    get text() {
        return this.getProperty('text')
    }

    get warning() {
        return this.supportWarnings ? this.getProperty('warning') : null
    }

    get information() {
        return this.getProperty('information')
    }

    get type() {
        return this.getProperty('type')
    }

    get disabled() {
        return this.getProperty('disabled') || false
    }

    get default() {
        return this.getProperty('default')
    }

    get floatingPoint() {
        return this.getProperty('floatingPoint')
    }

    get readonly() {
        try {
            return this.getProperty('readonly') || false
        } catch (e) {
            return false;
        }
    }

    set readonly(value) {
        try {
            this.setProperty('readonly', value);
        } catch (e) {
            return false;
        }
    } 

    /**
     * Executed every time user changes the field.
     *
     * @protected
     * @param {any} nextValue
     * @return {Boolean} Whether the store should be updated. Defaults to true.
     */
    onChange = nextValue => {
    }

    /**
     * Returns the global instance of the {@link Configurator} class.
     *
     * @protected
     * @return {Configurator}
     */
    getConfigurator() {
        return Configurator.getInstance()
    }

    /**
     * Returns the absolute path to this field.
     *
     * @protected
     * @return {String}
     */
    getUIPath() {
        return [this.step.name, this.name].join(Field.PathSeparator)
    }

    getModelPath() {
        return this.getUIPath()
    }

    /**
     * Returns value of specified property directly from the store.
     *
     * @protected
     * @param {String} name
     * @return {any}
     */
    getProperty(name) {
        if (this.formModelStore !== null) {
            return this.formModelStore.getFormModelProperty(this.getModelPath(), name);
        }
        return store.getFormModelProperty(this.getModelPath(), name)
    }

    setProperty(name, value) {
        if (this.formModelStore !== null) {
            return this.formModelStore.setFormModelProperty(this.getModelPath(), name, value);
        }
        return store.setFormModelProperty(this.getModelPath(), name, value)
    }

    /**
     * Returns properties to be passed to the React component that
     * renders this field.
     *
     * @private
     * @return {Object}
     */
    getComponentProperties() {

        return {
            ...this.getAdditionalComponentProperties(),

            value: this.value,
            unit: this.unit,
            visible: this.visible,
            items: this.items,
            label: this.label,
            description: this.description,
            text: this.text,
            warning: this.warning,
            information: this.information,
            type: this.type,
            disabled: this.disabled,
            readonly: this.readonly,
            errorState: this.errorState,
            validationErrorMessage: this.validationErrorMessage,
            required: this.required

        }
    }

    /**
     * Returns additional properties to pass to the React component
     * while rendering.
     *
     * @protected
     * @return {Object}
     */
    getAdditionalComponentProperties() {
        return {}
    }

    getStep() {
        return this.step
    }

    setRef(element) {
        this.ref = element;
    }

    getRef() {
        return this.ref;
    }

    /**
     * @return {Boolean}
     */
    hasWarning() {
        return Boolean(this.warning)
    }
}
